import React, { memo } from 'react';
import { useIntl } from 'react-intl';
import Markdown from 'markdown-to-jsx';

import Collapsible from '~components/collapsible';
import About from '../about';
import { visibleSections, seoVars } from './sections';
import { normalizeAiContent } from './aiContent';

// Server-rendered city SEO content, shown in every lifecycle state (0 / 1-3 / 4+
// ads). Renders from props + i18n only, so SSR and post-hydration output match.
// Pages with ads keep the compact collapsible header; the full layout renders on
// zero-ad pages, where this copy is the main page content.
const CitySeoContent = memo(({ city, isZeroAds = false, aiContent = null }) => {
  const intl = useIntl();

  if (!city) return null;

  const vars = seoVars(intl, city);
  const sections = visibleSections(intl.messages);

  // Backend-prepared city copy; when present it replaces the generic translated
  // body. The heading, CTA and FAQ stay as they are.
  const ai = normalizeAiContent(aiContent);

  const title = intl.formatMessage(
    { id: 'containers.city.seo.location.title', defaultMessage: 'Huishoudelijke hulp {city}' },
    vars
  );

  if (!isZeroAds && !ai) {
    if (!sections.includes('explainer')) {
      // Locale without the explainer copy: plain heading so nothing renders empty.
      return (
        <div className="c-city-seo">
          <h1 className="l-headline--primary">
            <Markdown options={{ forceBlock: false }}>{title}</Markdown>
          </h1>
        </div>
      );
    }

    return (
      <div className="c-city-seo">
        <Collapsible.Wrapper className="c-accordian-section">
          <Collapsible.Header
            iconAltText={intl.formatMessage({
              id: 'containers.city.seo.accordian-section.icon-alt',
              defaultMessage: 'Chevron Down',
            })}
          >
            <div className="c-accordian-section-item__headline">
              <h1>
                <Markdown options={{ forceBlock: false }}>{title}</Markdown>
              </h1>
            </div>
          </Collapsible.Header>
          <Collapsible.Content>
            <Markdown options={{ forceBlock: true }}>
              {intl.formatMessage({ id: 'containers.city.seo.accordian-section.content' }, vars)}
            </Markdown>
          </Collapsible.Content>
        </Collapsible.Wrapper>
      </div>
    );
  }

  return (
    <div className="c-city-seo">
      {/* Single canonical H1. Markdown with forceBlock:false so locale-level
          emphasis renders while the heading stays <h1>text</h1>. */}
      <h1 className="l-headline--primary">
        <Markdown options={{ forceBlock: false }}>{title}</Markdown>
      </h1>

      {ai ? (
        <div className="c-city-seo__ai">
          {ai.intro && <p className="c-city-seo__intro">{ai.intro}</p>}
          {ai.sections.map((section) => (
            <React.Fragment key={section.key}>
              <h2 className="l-headline--secondary">{section.heading}</h2>
              <p>{section.body}</p>
            </React.Fragment>
          ))}
        </div>
      ) : (
        sections.includes('intro') && (
          <p className="c-city-seo__intro">
            {intl.formatMessage({ id: 'containers.city.seo.location.sub-title' }, vars)}
          </p>
        )
      )}

      {!ai && sections.includes('explainer') && (
        <Collapsible.Wrapper className="c-accordian-section">
          <Collapsible.Header
            iconAltText={intl.formatMessage({
              id: 'containers.city.seo.accordian-section.icon-alt',
              defaultMessage: 'Chevron Down',
            })}
          >
            <div className="c-accordian-section-item__headline">
              <Markdown options={{ forceBlock: false }}>
                {intl.formatMessage({ id: 'containers.city.seo.accordian-section.headline' }, vars)}
              </Markdown>
            </div>
          </Collapsible.Header>
          <Collapsible.Content>
            <Markdown options={{ forceBlock: true }}>
              {intl.formatMessage({ id: 'containers.city.seo.accordian-section.content' }, vars)}
            </Markdown>
          </Collapsible.Content>
        </Collapsible.Wrapper>
      )}

      {!ai && sections.includes('about') && <About city={city} />}
    </div>
  );
});

export default CitySeoContent;
